import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { Form, Input, Select, Button, Card, App, Alert, Space, Upload, Progress, Segmented, Dropdown, Modal } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
const { TextArea } = Input;

const VideoEditor = () => {
    const [saving, setSaving] = useState(false);
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { slug: routeSlug, id: routeId } = useParams();
    const [searchParams] = window.location.search ? [new URLSearchParams(window.location.search)] : [new URLSearchParams()];
    // If slug is not in the path (e.g. /:id/edit), check the query string (?slug=...)
    const actualSlug = routeSlug || searchParams.get('slug');
    const [created, setCreated] = useState({ id: null, slug: '' });

    const [availableLangs, setAvailableLangs] = useState(['de','en','fr','it']);
    const [selectedLangs, setSelectedLangs] = useState(['de','en','fr','it']);
    const [activeLang, setActiveLang] = useState('en');

    const [i18nForms, setI18nForms] = useState({});
    const [uploadPct, setUploadPct] = useState(0);
    const [maxMb, setMaxMb] = useState(1024);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [phpLimits, setPhpLimits] = useState({ upload_max_filesize: null, post_max_size: null });
    const [fallbackThumbUrl, setFallbackThumbUrl] = useState('');
    const [pairLoading, setPairLoading] = useState(false);
    const [missingLoading, setMissingLoading] = useState(false);
    const videoRef = useRef(null);
    useEffect(() => {
        const initUploadLimits = async () => {
            try {
                const [vuRes, phpRes] = await Promise.all([
                    axios.get('/v1/settings/video-upload').catch(() => ({ data: { data: { max_mb: 1024 } } })),
                    axios.get('/v1/settings/php-upload-limits').catch(() => ({ data: { data: {} } }))
                ]);
                setMaxMb(vuRes.data?.data?.max_mb || 1024);
                setPhpLimits(phpRes.data?.data || {});
            } catch {}
        };
        initUploadLimits();
    }, []);
    const [sourceType, setSourceType] = useState('upload');

    useEffect(() => {
        const init = async () => {
            try {
                const res = await axios.get('/v1/settings/languages');
                let langs = res.data?.data;
                if (!langs || !Array.isArray(langs) || langs.length === 0) {
                    langs = ['de','en','fr','it'];
                }
                setAvailableLangs(langs);
                setSelectedLangs(langs);
                setActiveLang(langs.includes('en') ? 'en' : langs[0]);
                const forms = {};
                langs.forEach(k => { forms[k] = { title: '', description: '' }; });
                setI18nForms(forms);
                return langs; // Return for chaining
            } catch (e) {
                 const langs = ['de','en','fr','it'];
                 setAvailableLangs(langs);
                 setSelectedLangs(langs);
                 setActiveLang('en');
                 return langs;
            }
        };

        const loadVideo = async (langs) => {
            if (!actualSlug) return;
            try {
                const res = await axios.get(`/v1/videos/${actualSlug}`);
                const data = res.data?.data;
                if (data) {
                    setCreated({ id: data.id, slug: data.slug });
                    form.setFieldsValue({
                        category: data.category,
                        visibility: data.visibility || 'authenticated',
                        source_type: data.source_type || 'upload',
                        source_url: data.source_url
                    });
                    
                    const forms = {};
                    (langs || ['de','en','fr','it']).forEach(k => { forms[k] = { title: '', description: '' }; });
                    
                    if (data.translations) {
                        data.translations.forEach(t => {
                            if (forms[t.locale]) {
                                forms[t.locale] = {
                                    title: t.title || '',
                                    description: t.description || '',
                                    thumbnail_url: t.thumbnail_url || '',
                                    video_url: t.hls_manifest_url || t.source_url || '' // Map existing media
                                };
                            }
                        });
                    }
                    // Also ensure the base title/desc are in the correct language slot if known
                    if (data.language && forms[data.language]) {
                         // If translation didn't overwrite it, use base
                         if (!forms[data.language].title) forms[data.language].title = data.title;
                         if (!forms[data.language].description) forms[data.language].description = data.description;
                    }
                    
                    setI18nForms(prev => ({ ...prev, ...forms }));
                }
            } catch (e) {
                console.error(e);
                message.error('Failed to load video data');
            }
        };

        init().then((langs) => {
            if (actualSlug) loadVideo(langs);
        });
    }, [actualSlug]);

    const refreshCategoryOptions = async (lang) => {
        try {
            const catsRes = await axios.get('/v1/settings/video-categories');
            const cats = catsRes.data?.data || [];
            const active = lang || activeLang || 'en';
            setCategoryOptions(cats.map(c => ({ value: c.key, label: c.labels?.[active] || c.labels?.en || c.key })));
        } catch {}
    };

    useEffect(() => { refreshCategoryOptions(activeLang); }, [activeLang]);

    const submit = async (values) => {
        setSaving(true);
        try {
            console.log('VideoEditor submit called. activeLang:', activeLang);
            console.log('i18nForms:', i18nForms);
            console.log('Form values:', values);
            
            if (!activeLang) {
                console.error('CRITICAL: activeLang is missing/undefined!');
                message.error('System Error: Active language is missing. Check console.');
                setSaving(false);
                return;
            }

            if (created.id) {
                // Update
                await axios.put(`/v1/videos/${created.id}`, {
                    title: i18nForms[activeLang]?.title,
                    description: i18nForms[activeLang]?.description,
                    category: values.category,
                    language: activeLang, // Update base language if needed, or just leave as is
                    locale: activeLang, // Current locale being edited
                    visibility: values.visibility,
                    source_type: values.source_type,
                    source_url: values.source_type !== 'upload' ? values.source_url : null,
                });
                message.success('Updated');
            } else {
                // Create
                const res = await axios.post('/v1/videos', {
                    title: i18nForms[activeLang]?.title,
                    description: i18nForms[activeLang]?.description,
                    category: values.category,
                    language: activeLang,
                    locale: activeLang,
                    visibility: values.visibility,
                    source_type: values.source_type,
                    source_url: values.source_type !== 'upload' ? values.source_url : null,
                });
                const id = res.data?.data?.id;
                const slug = res.data?.data?.slug || '';
                setCreated({ id, slug });
                message.success(`Saved. Slug: ${slug}`);
                navigate(`/admin/videos/edit/${slug}`);
            }
        } catch (err) {
            console.error(err);
            message.error(`Save failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const saveTranslations = async () => {
        if (!created.id) { message.warning('Save the video basics first.'); return; }
        const translations = selectedLangs.map((locale) => ({ locale, ...i18nForms[locale] }));
        try {
            await axios.post(`/v1/videos/${created.id}/translations`, { translations });
            message.success('Translations saved');
        } catch (e) { message.error('Save translations failed'); }
    };

    const aiTranslateMissing = async (fromLocale) => {
        if (!created.id) { message.warning('Save the video basics first.'); return; }
        const src = i18nForms[fromLocale] || { title: '', description: '' };
        if (!src.title && !src.description) {
            Modal.warning({ title: 'Source language is empty', content: `Please enter title or description for ${fromLocale.toUpperCase()} first.` });
            return;
        }
        setMissingLoading(true);
        const updates = { ...i18nForms };
        for (const locale of selectedLangs) {
            if (locale === fromLocale) continue;
            const dest = updates[locale] || { title: '', description: '' };
            if (!dest.title || !dest.description) {
                try {
                    const res = await axios.post(`/v1/videos/${created.id}/translate`, { source_locale: fromLocale, target_locale: locale });
                    const data = res.data?.data;
                    if (data?.locale === locale) {
                        updates[locale] = {
                            title: (dest.title && dest.title.trim().length) ? dest.title : (data.title || ''),
                            description: (dest.description && dest.description.trim().length) ? dest.description : (data.description || ''),
                        };
                    }
                } catch (e) {
                    // continue other languages
                }
            }
        }
        setI18nForms(updates);
        message.success('Translated missing fields');
        setMissingLoading(false);
    };

    // Pairwise translate: into activeLang only filling empty fields
    const aiTranslatePair = async (fromLocale) => {
        if (!created.id) { message.warning('Save the video basics first.'); return; }
        if (fromLocale === activeLang) { message.info('Select different languages'); return; }
        const dest = i18nForms[activeLang] || { title: '', description: '' };
        if (dest.title && dest.description) { message.info('Nothing to translate. All fields are filled.'); return; }
        setPairLoading(true);
        try {
            const res = await axios.post(`/v1/videos/${created.id}/translate`, { source_locale: fromLocale, target_locale: activeLang });
            const data = res.data?.data;
            if (data?.locale === activeLang) {
                setI18nForms(prev => ({
                    ...prev,
                    [activeLang]: {
                        title: (prev[activeLang]?.title && prev[activeLang]?.title.trim().length) ? prev[activeLang]?.title : (data.title || ''),
                        description: (prev[activeLang]?.description && prev[activeLang]?.description.trim().length) ? prev[activeLang]?.description : (data.description || ''),
                    }
                }));
                message.success(`Translated ${fromLocale.toUpperCase()} → ${activeLang.toUpperCase()}`);
            }
        } catch (e) { message.error('AI translate failed'); }
        finally { setPairLoading(false); }
    };

    const handleUpload = async ({ file }) => {
        if (!created.id) { message.warning('Save the video basics first.'); return; }
        const maxBytes = (maxMb || 1024) * 1024 * 1024;
        if (file.size > maxBytes) { message.error(`File is too large. Max allowed is ${maxMb} MB.`); return; }
        const formData = new FormData(); formData.append('file', file);
        try {
            const res = await axios.post(`/v1/videos/${created.id}/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => { if (e.total) setUploadPct(Math.round((e.loaded * 100) / e.total)); }
            });
            message.success('Upload complete');
            const data = res.data?.data;
            if (data?.url) {
                // Inform the user and set form fields appropriately
                form.setFieldsValue({ source_type: 'upload', source_url: null });
                setPreviewUrl(data.url);
            }
        } catch (e) {
            const status = e?.response?.status;
            const ex = e?.response?.data?.exception || '';
            if (status === 413 || String(ex).includes('PostTooLargeException')) {
                const um = phpLimits.upload_max_filesize || 'server default';
                const pm = phpLimits.post_max_size || 'server default';
                message.error(`Upload failed: file exceeds server limit (upload_max_filesize=${um}, post_max_size=${pm}).`);
            } else {
                message.error('Upload failed');
            }
        }
    };

    const moreLangOptions = useMemo(() => {
        const remaining = availableLangs.filter(k => !selectedLangs.includes(k));
        return remaining.map(k => ({ key: k, label: k.toUpperCase(), onClick: () => setSelectedLangs([...selectedLangs, k]) }));
    }, [availableLangs, selectedLangs]);

    return (
        <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
            <style>{`@media (min-width: 992px){ .video-create-grid{ grid-template-columns: 1.1fr 0.9fr !important; } }`}</style>
            <div className="video-create-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                <Card title="New Video" variant="outlined">
                    {created.slug && (
                        <Alert
                            type="success"
                            showIcon
                            message={<span>Created: <a href={`/videos/${created.slug}`} target="_blank" rel="noopener noreferrer">/videos/{created.slug}</a></span>}
                            style={{ marginBottom: 12 }}
                        />
                    )}
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={submit}
                        onValuesChange={(changedValues) => {
                            if (Object.prototype.hasOwnProperty.call(changedValues, 'source_type')) {
                                setSourceType(changedValues.source_type);
                            }
                        }}
                    >
                    <Form.Item label={`Title (${activeLang})`} required>
                        <Input
                            maxLength={200}
                            value={i18nForms[activeLang]?.title || ''}
                            onChange={(e) => setI18nForms({ ...i18nForms, [activeLang]: { ...i18nForms[activeLang], title: e.target.value } })}
                        />
                    </Form.Item>
                    <Form.Item label={`Description (${activeLang})`}>
                        <TextArea
                            rows={6}
                            value={i18nForms[activeLang]?.description || ''}
                            onChange={(e) => setI18nForms({ ...i18nForms, [activeLang]: { ...i18nForms[activeLang], description: e.target.value } })}
                        />
                    </Form.Item>
                    <Form.Item name="category" label="Category">
                        <Select
                            showSearch
                            placeholder="Select a category"
                            onOpenChange={(open) => { if (open) { refreshCategoryOptions(activeLang); } }}
                            options={categoryOptions}
                        />
                    </Form.Item>
                    <Form.Item name="visibility" label="Visibility" initialValue="authenticated">
                        <Select options={[{ value: 'public', label: 'Public' }, { value: 'authenticated', label: 'Authenticated' }, { value: 'role-restricted', label: 'Role-restricted' }]} />
                    </Form.Item>
                    <Form.Item name="source_type" label="Source Type" initialValue="upload">
                        <Select options={[{ value: 'upload', label: 'Upload (processing)' }, { value: 'youtube', label: 'YouTube' }, { value: 'vimeo', label: 'Vimeo' }]} />
                    </Form.Item>
                    {sourceType !== 'upload' ? (
                        <Form.Item name="source_url" label="Source URL">
                            <Input placeholder="https://..." />
                        </Form.Item>
                    ) : null}
                    {sourceType === 'upload' && (
                        <div style={{ marginBottom: 16 }}>
                            <Upload.Dragger multiple={false} showUploadList={false} customRequest={handleUpload} accept="video/*">
                                <p>Drag and drop video here or click to select</p>
                            </Upload.Dragger>
                            {uploadPct > 0 && <Progress percent={uploadPct} style={{ marginTop: 12 }} />}
                            <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>Max size: {maxMb} MB</div>
                        </div>
                    )}
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', marginBottom: 6 }}>Thumbnail (fallback)</label>
                        <Upload.Dragger
                            multiple={false}
                            showUploadList={false}
                            customRequest={async ({ file }) => {
                                if (!created.id) { message.warning('Save the video basics first.'); return; }
                                const fd = new FormData(); fd.append('file', file);
                                try {
                                    const res = await axios.post(`/v1/videos/${created.id}/thumbnail`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                    const url = res.data?.data?.url;
                                    if (url) { setFallbackThumbUrl(url); }
                                    message.success('Fallback thumbnail uploaded');
                                } catch { message.error('Thumbnail upload failed'); }
                            }}
                            accept="image/*"
                            style={{ minHeight: 140, height: 140, position: 'relative' }}
                        >
                            {fallbackThumbUrl ? (
                                <img src={fallbackThumbUrl} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                            ) : (
                                <p>Drop a JPG/PNG/WEBP or click to select</p>
                            )}
                        </Upload.Dragger>
                    </div>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
                        </Form.Item>
                    </Form>
                </Card>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Card title="Translations" variant="outlined">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Segmented size="middle" options={selectedLangs.map(k => ({ label: k.toUpperCase(), value: k }))} value={activeLang} onChange={(val) => setActiveLang(val)} />
                    <Dropdown menu={{ items: moreLangOptions }}>
                        <Button icon={<DownOutlined />}>More</Button>
                    </Dropdown>
                </div>
                <Space direction="vertical" style={{ width: '100%' }}>
                    {/* Title/Desc are now in the main card, bound to i18nForms */}
                    <div style={{ color: '#888', fontStyle: 'italic' }}>Switch language above to edit Title/Description in the main "New Video" card.</div>
                </Space>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12, marginBottom: 40, position: 'relative', zIndex: 0 }}>
                    <div style={{ marginBottom: 8 }}>
                        <label style={{ display: 'block', marginBottom: 6 }}>Video for {activeLang.toUpperCase()}</label>
                        <Upload.Dragger
                            multiple={false}
                            showUploadList={false}
                            customRequest={async ({ file }) => {
                                if (!created.id) { message.warning('Save the video basics first.'); return; }
                                const fd = new FormData(); fd.append('file', file); fd.append('locale', activeLang);
                                try {
                                    const up = await axios.post(`/v1/videos/${created.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                    const url = up.data?.data?.url;
                                    if (url) {
                                        setI18nForms(prev => ({ ...prev, [activeLang]: { ...prev[activeLang], video_url: url } }));
                                        setPreviewUrl(url);
                                    }
                                    message.success('Localized video uploaded');
                                } catch { message.error('Localized video upload failed'); }
                            }}
                            accept="video/*"
                            style={{ minHeight: 140, height: 140, position: 'relative' }}
                        >
                            {i18nForms[activeLang]?.video_url ? (
                                <video src={i18nForms[activeLang]?.video_url} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                            ) : (
                                <p>Drop a video or click to select</p>
                            )}
                        </Upload.Dragger>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                        <label style={{ display: 'block', marginBottom: 6 }}>Thumbnail for {activeLang.toUpperCase()}</label>
                        <Upload.Dragger
                            multiple={false}
                            showUploadList={false}
                            customRequest={async ({ file }) => {
                                if (!created.id) { message.warning('Save the video basics first.'); return; }
                                const fd = new FormData(); fd.append('file', file); fd.append('locale', activeLang);
                                try {
                                    const res = await axios.post(`/v1/videos/${created.id}/thumbnail`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                    const url = res.data?.data?.url;
                                    if (url) {
                                        setI18nForms(prev => ({ ...prev, [activeLang]: { ...prev[activeLang], thumbnail_url: url } }));
                                    }
                                    message.success('Localized thumbnail uploaded');
                                } catch { message.error('Thumbnail upload failed'); }
                            }}
                            accept="image/*"
                            style={{ minHeight: 140, height: 140, position: 'relative' }}
                        >
                            {i18nForms[activeLang]?.thumbnail_url ? (
                                <img src={i18nForms[activeLang]?.thumbnail_url} alt={`Thumbnail ${activeLang}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none', borderRadius: 8 }} />
                            ) : (
                                <p>Drop a JPG/PNG/WEBP or click to select</p>
                            )}
                        </Upload.Dragger>
                    </div>
                </div>
                <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button type="primary" onClick={saveTranslations}>Save translations</Button>
                    <Dropdown
                        menu={{
                            items: availableLangs.filter(l => l !== activeLang).map(l => ({
                                key: l,
                                label: `AI translate from ${l.toUpperCase()} → ${activeLang.toUpperCase()}`,
                                onClick: () => aiTranslatePair(l)
                            }))
                        }}
                    >
                        <Button loading={pairLoading}>AI Translate</Button>
                    </Dropdown>
                    <Dropdown
                        menu={{
                            items: selectedLangs.map(l => ({
                                key: l,
                                label: `AI translate missing from ${l.toUpperCase()} → others`,
                                onClick: () => aiTranslateMissing(l)
                            }))
                        }}
                    >
                        <Button loading={missingLoading}>AI Translate Missing</Button>
                    </Dropdown>
                </div>
                    </Card>
                    <Card title="Preview" variant="outlined">
                        {previewUrl ? (
                            <video ref={videoRef} controls style={{ width: '100%', borderRadius: 12, background: '#000', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                                <source src={previewUrl} />
                            </video>
                        ) : (
                            <div style={{ color: '#888' }}>{created.id ? 'Upload a file to preview.' : 'Save first, then upload to preview.'}</div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default VideoEditor;
